import { AxiosError } from 'axios';
import Area from '../../domain/entities/Area';
import Merchant from '../../domain/entities/Merchant';
import MerchantCategory from '../../domain/entities/MerchantCategory';
import Review from '../../domain/entities/Review';
import BlogPost from '../../domain/entities/BlogPost';
import Product from '../../domain/entities/Product';
import BookingService from '../../domain/entities/BookingService';
import Rating from '../../domain/entities/Rating';
import Pagination from '../../domain/valueobjects/Pagination';
import { newUcMerchantApi, UcMerchantsApi } from '../api/backend/api';
import Regulation from '../../domain/entities/regulation/Regulation';
import Course from '../../domain/entities/Course';
import Staff from '../../domain/entities/Staff';
import MapWidget from '../../domain/entities/MapWidget';

export interface LayoutResponse {
  merchant: Merchant;
  area?: Area;
  category: MerchantCategory;
  bookingServiceCount: number;
  productCount: number;
  blogPostCount: number;
}

export interface MerchantReviewResponse {
  reviews: Review[];
  pagination: Pagination;
}

export interface TopContentResponse {
  reviews: Review[];
}

export interface SingleBlogResponse {
  blogPost: BlogPost;
}

export interface MultiBlogResponse {
  blogPosts: BlogPost[];
  pagination: Pagination;
}

export interface MultiProductResponse {
  products: Product[];
  pagination: Pagination;
}

export interface SingleProductResponse {
  isPasswordProtected: boolean;
  product: Product;
  bookingServices: BookingService[];
  productStatus: string;
}

export interface SingleBookingServiceResponse {
  isPasswordProtected: boolean;
  isBookable: boolean;
  bookingService: BookingService;
  reviews: Review[];
  rating: Rating;
  regulation: Regulation;
  courses: Course[];
  staff: Staff[];
  mapWidget?: MapWidget;
}

export class UCMerchantRepository {
  constructor(private api: UcMerchantsApi) {}

  public async Layout(publicId: string): Promise<LayoutResponse> {
    return this.api
      .merchantsLayout(publicId)
      .then(res => {
        return {
          merchant: new Merchant(res.data.merchant),
          area: res.data.area ? new Area(res.data.area) : undefined,
          category: new MerchantCategory(res.data.merchantCategory),
          bookingServiceCount: res.data.bookingServiceCount,
          productCount: res.data.productCount,
          blogPostCount: res.data.blogPostCount,
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }

  public async Review(
    publicId: string,
    page?: number,
    limit?: number,
  ): Promise<MerchantReviewResponse> {
    return this.api
      .merchantsReview(publicId, page, limit)
      .then(res => {
        return {
          reviews: res.data.reviews.map(r => {
            return new Review(r);
          }),
          pagination: res.data.pagination,
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }

  public async TopContent(publicId: string): Promise<TopContentResponse> {
    return this.api
      .merchantsTopContent(publicId)
      .then(res => {
        return {
          reviews: res.data.reviews.map(r => {
            return new Review(r);
          }),
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }

  public async Blog(
    publicId: string,
    blogId: number,
  ): Promise<SingleBlogResponse> {
    return this.api
      .merchantsBlog(publicId, blogId)
      .then(res => {
        return {
          blogPost: new BlogPost(res.data.blogPost),
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }

  public async Blogs(
    publicId: string,
    page?: number,
  ): Promise<MultiBlogResponse> {
    return this.api
      .merchantsBlogs(publicId, page)
      .then(res => {
        return {
          blogPosts: res.data.blogPosts.map(r => {
            return new BlogPost(r);
          }),
          pagination: res.data.pagination,
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }

  public async Products(
    publicId: string,
    page?: number,
  ): Promise<MultiProductResponse> {
    return this.api
      .merchantsProducts(publicId, page)
      .then(res => {
        return {
          products: res.data.products.map(r => {
            return new Product(r);
          }),
          pagination: res.data.pagination,
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }

  public async Product(
    publicId: string,
    productPublicId: number,
  ): Promise<SingleProductResponse> {
    return this.api
      .merchantsProduct(publicId, productPublicId)
      .then(res => {
        return {
          isPasswordProtected: res.data.isPasswordProtected,
          product: new Product(res.data.product),
          bookingServices: res.data.bookingServices.map(service => {
            return new BookingService(service);
          }),
          productStatus: res.data.productStatus,
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }

  public async BookingService(
    publicId: string,
    servicePublicId: number,
  ): Promise<SingleBookingServiceResponse> {
    return this.api
      .merchantsService(publicId, servicePublicId)
      .then(res => {
        return {
          isPasswordProtected: res.data.isPasswordProtected,
          isBookable: res.data.isBookable,
          bookingService: new BookingService(res.data.bookingService),
          reviews: res.data.reviews.map(review => {
            return new Review(review);
          }),
          rating: new Rating(res.data.rating),
          regulation: new Regulation({
            ...res.data.regulation,
          }),
          courses: res.data.courses.map(c => {
            return new Course(c);
          }),
          staff: res.data.staff.map(s => {
            return new Staff(s);
          }),
          mapWidget: res.data.mapWidget
            ? new MapWidget(res.data.mapWidget)
            : undefined,
        };
      })
      .catch((err: AxiosError) => {
        // TODO handle error by status
        return Promise.reject(err);
      })
      .catch((err: Error) => {
        return Promise.reject(err);
      });
  }
}

export default function(session?: string, token?: string) {
  return new UCMerchantRepository(newUcMerchantApi(session, token));
}
